import { cx } from "../../utils";

type SortKey = "RequestNumber" | "MallName" | "TenantName" | "RequestStatusName";

interface TenantRequestData {
  TenantRequestId: number;
  RequestNumber: string;
  MallName: string;
  TenantName: string;
  Notes: string;
  RequestStatusName?: string;
  RequestStatusId?: number;
  Status: number;
  CreatedOn?: string;
}

interface TenantRequestTableProps {
  pageRows: TenantRequestData[];
  selected: Record<string, boolean>;
  setSelected: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  toggleSort: (key: SortKey) => void;
  getStatusLabelById: (statusId: number | undefined | null, rawStatusName?: string) => string;
  sorted: TenantRequestData[];
  perPage: number;
  setPerPage: (perPage: number) => void;
  page: number;
  setPage: (page: number) => void;
  clampedPage: number;
  totalPages: number;
  start: number;
  end: number;
}

const TenantRequestTable = ({
  pageRows,
  selected,
  setSelected,
  sortKey,
  sortDir,
  toggleSort,
  getStatusLabelById,
  sorted,
  perPage,
  setPerPage,
  page,
  setPage,
  clampedPage,
  start,
  end,
}: TenantRequestTableProps) => {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="text-xs uppercase text-gray-500 dark:text-gray-400">
              <th className="py-3 pl-4 pr-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                    checked={pageRows.length > 0 && pageRows.every((r) => selected[r.TenantRequestId])}
                    ref={(el) => {
                      if (el) {
                        const all = pageRows.length > 0 && pageRows.every((r) => selected[r.TenantRequestId]);
                        const some = pageRows.some((r) => selected[r.TenantRequestId]) && !all;
                        el.indeterminate = some;
                      }
                    }}
                    onChange={() => {
                      const all = pageRows.length > 0 && pageRows.every((r) => selected[r.TenantRequestId]);
                      const next = { ...selected };
                      pageRows.forEach((r) => (next[r.TenantRequestId] = !all));
                      setSelected(next);
                    }}
                  />
                </label>
              </th>
              <Th sortable activeKey={sortKey} dir={sortDir} k="RequestNumber" onClick={toggleSort}>
                Request Number
              </Th>
              <Th sortable activeKey={sortKey} dir={sortDir} k="MallName" onClick={toggleSort}>
                Mall
              </Th>
              <Th sortable activeKey={sortKey} dir={sortDir} k="TenantName" onClick={toggleSort}>
                Tenant
              </Th>
              <th className="py-3 px-4">Notes</th>
              <th className="py-3 px-4">Request Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {pageRows.map((e) => {
              const statusLabel = getStatusLabelById(e.RequestStatusId, e.RequestStatusName);
              return (
                <tr key={e.TenantRequestId} className="text-sm text-gray-800 dark:text-gray-100">
                  <td className="py-3 pl-4 pr-2">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      checked={!!selected[e.TenantRequestId]}
                      onChange={(ev) =>
                        setSelected((prev) => ({
                          ...prev,
                          [e.TenantRequestId]: ev.target.checked,
                        }))
                      }
                    />
                  </td>
                  <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{e.RequestNumber}</td>
                  <td className="py-3 px-4 text-gray-700 dark:text-gray-200">{e.MallName}</td>
                  <td className="py-3 px-4 text-gray-700 dark:text-gray-200">{e.TenantName}</td>
                  <td className="py-3 px-4 text-gray-600 dark:text-gray-300 max-w-xs truncate">{e.Notes || "-"}</td>
                  <td className="py-3 px-4 text-gray-700 dark:text-gray-200">
                    {statusLabel || "-"}
                  </td>
                </tr>
              );
            })}

            {pageRows.length === 0 && (
              <tr>
                <td className="py-10 px-4 text-center text-sm text-gray-500 dark:text-gray-400" colSpan={6}>
                  Tidak ada request yang cocok dengan filter / pencarian.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing{" "}
          <span className="font-medium text-gray-800 dark:text-gray-200">{sorted.length === 0 ? 0 : start + 1}</span>{" "}
          to{" "}
          <span className="font-medium text-gray-800 dark:text-gray-200">{end}</span>{" "}
          of{" "}
          <span className="font-medium text-gray-800 dark:text-gray-200">{sorted.length}</span>{" "}
          requests
        </div>

        <div className="flex items-center gap-2">
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>

          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden dark:border-gray-700">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={clampedPage === 1}
              className={cx(
                "px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200",
                clampedPage === 1
                  ? "text-gray-300 bg-gray-50 cursor-not-allowed dark:text-gray-500 dark:bg-gray-800"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              Previous
            </button>
            <div className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 border-l border-r border-gray-200 dark:border-gray-700">
              Page {clampedPage} / {Math.max(1, Math.ceil(sorted.length / perPage))}
            </div>
            <button
              onClick={() =>
                setPage(Math.min(Math.max(1, Math.ceil(sorted.length / perPage)), page + 1))
              }
              disabled={clampedPage === Math.max(1, Math.ceil(sorted.length / perPage))}
              className={cx(
                "px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200",
                clampedPage === Math.max(1, Math.ceil(sorted.length / perPage))
                  ? "text-gray-300 bg-gray-50 cursor-not-allowed dark:text-gray-500 dark:bg-gray-800"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

function Th({
  children,
  sortable,
  k,
  activeKey,
  dir,
  onClick,
}: {
  children: React.ReactNode;
  sortable?: boolean;
  k?: SortKey;
  activeKey?: SortKey;
  dir?: "asc" | "desc";
  onClick?: (k: SortKey) => void;
}) {
  const isActive = sortable && k === activeKey;
  return (
    <th
      className={cx("py-3 px-4 select-none text-gray-600 dark:text-gray-300", sortable && "cursor-pointer hover:text-gray-700 dark:hover:text-gray-200")}
      onClick={sortable && k && onClick ? () => onClick(k as SortKey) : undefined}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortable && (
          <svg viewBox="0 0 24 24" className={cx("w-3.5 h-3.5 text-gray-400 dark:text-gray-500", isActive && "text-gray-600 dark:text-gray-300")} fill="none" stroke="currentColor" strokeWidth="2">
            {isActive ? dir === "asc" ? <path d="M12 7l-5 5h10l-5-5z" /> : <path d="M12 17l5-5H7l5 5z" /> : <>
              <path d="M12 7l-5 5h10l-5-5z" />
              <path d="M12 17l5-5H7l5 5z" />
            </>}
          </svg>
        )}
      </span>
    </th>
  );
}

export default TenantRequestTable;